import { matchIsValidTel } from 'mui-tel-input';

import { FIELD_TYPES } from '../../constants';
import { getTitleUserMaxLength } from '../../utils';

// Kept intentionally simple (not RFC 5322): good enough to catch missing
// "@"/domain typos without rejecting valid-but-unusual addresses.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isEmptyValue = (field, value) => {
  if (field.type === FIELD_TYPES.FILE) {
    return !Array.isArray(value) || value.length === 0;
  }

  return typeof value !== 'string' || value.trim().length === 0;
};

const getEffectiveMaxLength = (field, options = {}) => {
  const maxLength = field.validation?.maxLength;
  if (maxLength == null) {
    return undefined;
  }

  if (field.id === 'title' && options.titlePrefix != null) {
    return getTitleUserMaxLength(maxLength, options.titlePrefix);
  }

  return maxLength;
};

// Returns an error message for a single field/value pair, or undefined when valid.
// readOnly fields are never user-editable (their value comes from config), so
// they're excluded from validation entirely.
export const validateField = (field, value, options = {}) => {
  if (field.readOnly) {
    return undefined;
  }

  if (field.required && isEmptyValue(field, value)) {
    return `${field.label} is required.`;
  }

  // Format checks below only apply once a value is present - an empty,
  // non-required field is valid.
  if (isEmptyValue(field, value)) {
    return undefined;
  }

  const effectiveMaxLength = getEffectiveMaxLength(field, options);
  if (effectiveMaxLength != null && typeof value === 'string' && value.length > effectiveMaxLength) {
    return `${field.label} must be at most ${effectiveMaxLength} characters.`;
  }

  if (field.type === FIELD_TYPES.EMAIL && !EMAIL_REGEX.test(value.trim())) {
    return 'Enter a valid email address.';
  }

  if (field.type === FIELD_TYPES.PHONE && !matchIsValidTel(value)) {
    return 'Enter a valid phone number.';
  }

  return undefined;
};

// Returns a { [fieldId]: message } map containing only the fields that are
// currently invalid.
export const validateFields = (fields = [], values = {}, options = {}) =>
  fields.reduce((errors, field) => {
    const message = validateField(field, values[field.id], options);
    if (message) {
      errors[field.id] = message;
    }

    return errors;
  }, {});
